import {
  DefaultButton,
  DialogFooter,
  mergeStyleSets,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Stack,
  Text,
} from "@fluentui/react";
import { ExperimentType, PipelineStatus } from "../../Types";
import { EnvStatus } from "./ArchiveExperimentConfirmationModal";
import { statusRenderer } from "../../Experiment/RolloutDetails/RolloutDetailsTable/rolloutDetailsColumnRenderers";
import { formatDate } from "../../Experiment/RolloutDetails/rolloutDetails.utils";
import { useMemo } from "react";

interface ArchiveExperimentsConfirmRunningProps {
  /**
   * The experiment to be archived.
   */
  experiment: ExperimentType;
  /**
   * Map of environment names to their experiment status.
   */
  statusMap: Record<string, EnvStatus | null>;
  /**
   * Callback function to continue with the archiving process.
   */
  onContinue: () => void;
  /**
   * Callback function to cancel the archiving process.
   */
  onCancel: () => void;
}

/**
 * Confirmation dialog component to notify users of running experiments
 * before archiving an experiment. They will need to confirm this dialog in order
 * to continue with archiving.
 */
const ArchiveExperimentsConfirmRunning: React.FC<
  ArchiveExperimentsConfirmRunningProps
> = ({ statusMap, onContinue, onCancel }) => {
  const envEntries = useMemo(() => Object.entries(statusMap), [statusMap]);
  return (
    <Stack>
      <MessageBar messageBarType={MessageBarType.warning}>
        Experiments are still running in the following environments. It’s
        recommended to rollback all running experiments before archiving since
        you will no longer be able to manage the experiments while archived.
      </MessageBar>
      <table className={styles.statusContainer}>
        <thead>
          <tr>
            <th>Env</th>
            <th>Treatment</th>
            <th>Start Time</th>
          </tr>
        </thead>
        <tbody>
          {envEntries.map(([env, status]) => (
            <EnvStatusRow key={env} env={env} status={status} />
          ))}
        </tbody>
      </table>
      <Text>Would you still like to continue archiving the experiment?</Text>
      <DialogFooter>
        <PrimaryButton
          text="Yes, continue"
          onClick={onContinue}
          data-testid="confirm-archive-button"
        />
        <DefaultButton
          text="No, cancel"
          onClick={onCancel}
          data-testid="cancel-archive-button"
        />
      </DialogFooter>
    </Stack>
  );
};

/**
 * Row component to display the status of an experiment in a specific environment.
 */
const EnvStatusRow: React.FC<{
  env: string;
  status: EnvStatus | null;
}> = ({ env, status }) => (
  <tr>
    <td>
      {statusRenderer(
        env,
        status?.isRunning ? PipelineStatus.WARNING : PipelineStatus.COMPLETED,
        false,
        undefined,
        undefined,
        status?.isRunning
          ? "Recommend rolling back experiment in this environment"
          : "Not running, no action needed",
      )}
    </td>
    <td>
      <Text>{status?.treatment || "Not started"}</Text>
    </td>
    <td>
      <Text variant="small">
        {status?.startTime ? formatDate(status.startTime) : undefined}
      </Text>
    </td>
  </tr>
);

const styles = mergeStyleSets({
  statusContainer: {
    margin: "15px 0",
  },
});

export default ArchiveExperimentsConfirmRunning;
